import MetaradarStateItem from '@app/model/metaradar/MetaradarStateItem';
import DataProvider from '@app/model/_common/DataProvider';
import Region from '@app/model/market_state/Region';

export class MetaradarDailyStateSnapshot {
	stateId: number;
	statDate: string;
	items: MetaradarStateItem[] = [];

	constructor(data: MetaradarDailyStateSnapshot) {
		Object.assign(this, data);
	}

	public findBrandOfferPerRegion(): Map<Region, MetaradarStateItem | undefined> {
		const result = new Map<Region, MetaradarStateItem | undefined>();

		const regions = [...new Set(this.items.map(item => item.region))];

		for (const region of regions) {
			const brandOfferItem = this.items.find(item => item.isBrandOffer && item.region === region);
			if (!!brandOfferItem) result.set(region, brandOfferItem);
		}

		return result;
	}

	public isUndercutByOneOfTheProviders(providers: DataProvider[], threshold: number) {
		const brandOfferItem = this.items.find(item => item.isBrandOffer);
		if (!brandOfferItem) return false;
		const undercutItems = this.items.filter(item => providers.includes(item.provider) && this.filter(item, brandOfferItem, threshold));
		return undercutItems.length > 0;
	}

	public countProviderItems(provider: DataProvider) {
		return this.items.filter(item => item.provider === provider).length;
	}

	public countProviderPerRegionItems(provider: DataProvider, region: Region) {
		return this.items.filter(item => item.provider === provider && item.region === region).length;
	}

	private filter(item: MetaradarStateItem, brandOffer: MetaradarStateItem, threshold: number) {
		return !item.isBrandOffer && !item.isSoldOut && !!item.price && (Number(item.price.amount) + Number(threshold)) < Number(brandOffer.price.amount);
	}

	public findUndercuts(threshold: number): {
		brandOffer: MetaradarStateItem;
		undercuts: MetaradarStateItem[]
	}[] | undefined {
		const brandOfferPerRegion = this.findBrandOfferPerRegion();

		// Check if there is at least one valid brand offer that is not sold out
		const hasValidBrandOffer = Array.from(brandOfferPerRegion.values()).some(bo => bo && !bo.isSoldOut);

		if (!hasValidBrandOffer) {
			return undefined;
		}

		const allUndercuts: { brandOffer: MetaradarStateItem; undercuts: MetaradarStateItem[] }[] = [];

		for (const [region, brandOffer] of brandOfferPerRegion.entries()) {
			if (brandOffer && !brandOffer.isSoldOut) {
				const undercuts = this.items.filter(item => item.region === region && this.filter(item, brandOffer, threshold));
				if (undercuts.length > 0) {
					allUndercuts.push({ brandOffer, undercuts });
				}
			}
		}

		return allUndercuts;
	}

	public findDifferencePerProvider(threshold: number): Map<DataProvider, {
		totalDifference: number,
		count: number,
		iconUrl: string
	}> {
		const result = new Map<DataProvider, { totalDifference: number, count: number, iconUrl: string }>();

		const providers = [...new Set(this.items.map(item => item.provider))];

		for (const provider of providers) {
			const diff = this.findDifferenceForProvider(threshold, provider);
			if (diff) {
				result.set(provider, diff);
			}
		}

		return result;
	}

	public findDifferenceForProvider(threshold: number, provider: DataProvider): {
		totalDifference: number,
		count: number,
		iconUrl: string
	} | null {
		const brandOfferPerRegion = this.findBrandOfferPerRegion();
		if (brandOfferPerRegion.size === 0) {
			return null;
		}

		let totalDifference = 0;
		let count = 0;
		let iconUrl = '';

		const providerItems = this.items.filter(item => item.provider === provider);

		for (const item of providerItems) {
			const brandOffer = brandOfferPerRegion.get(item.region);

			if (brandOffer && !brandOffer.isSoldOut && brandOffer.price) {
				if (this.filter(item, brandOffer, threshold)) {
					const difference = brandOffer.price.amount - item.price!.amount;
					const percentageDifference = (difference / brandOffer.price.amount) * 100;
					totalDifference += percentageDifference;
					count++;
					iconUrl = item.iconUrl;
				}
			}
		}

		if (count === 0) {
			return null;
		}

		return {
			totalDifference,
			count,
			iconUrl,
		};
	}

	public countUndercutRegions(providers: DataProvider[], threshold: number): { undercutRegions: number, totalRegions: number } {
		const allRegions = [...new Set(this.items.map(item => item.region))];
		if (allRegions.length === 0) {
			return { undercutRegions: 0, totalRegions: 0 };
		}

		const undercutRegions = allRegions.filter(region => {
			const brandOfferForRegion = this.items.find(item => item.region === region && item.isBrandOffer);
			if (!brandOfferForRegion || brandOfferForRegion.isSoldOut || !brandOfferForRegion.price) {
				return false;
			}

			return this.items.some(item =>
				item.region === region &&
				providers.includes(item.provider) &&
				this.filter(item, brandOfferForRegion, threshold)
			);
		});

		return { undercutRegions: undercutRegions.length, totalRegions: allRegions.length };
	}

}